import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Box, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { useQueryClient } from '@tanstack/react-query';

import { patchUser } from '@uplift/api';
import { useCurrentuser, useCurrentuserRefetch } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { Button, Chip, Modal, ModalContent, SubmitButton, TextInput, useAlert } from '@uplift/ui-v2';
import { VALIDATION_FIRST_NAME, VALIDATION_LAST_NAME } from '@uplift/validations';

import { isPlaceholderFirstName, isPlaceholderLastName } from '../../../../hooks';

type MissingNameFormValues = {
  firstName: string;
  lastName: string;
};

export type MissingNameModalProps = {
  onClose: () => void;
};

export const MissingNameModal: React.FC<MissingNameModalProps> = ({ onClose }) => {
  const l10n = useL10n();
  const copy = l10n.dashboard.actionItems.missingName.modal;
  const { classes } = useStyles();
  const me = useCurrentuser();
  const { refetchUser } = useCurrentuserRefetch();
  const queryClient = useQueryClient();
  const alert = useAlert();
  const validationSchema = React.useMemo(() => Yup.object({
    firstName: VALIDATION_FIRST_NAME.test(
      'not-placeholder',
      copy.invalidFirstName,
      value => !isPlaceholderFirstName(value)
    ),
    lastName: VALIDATION_LAST_NAME.test(
      'not-placeholder',
      copy.invalidLastName,
      value => !isPlaceholderLastName(value)
    ),
  }), [ copy.invalidFirstName, copy.invalidLastName ]);

  const initialValues: MissingNameFormValues = {
    firstName: isPlaceholderFirstName(me.firstName) ? '' : me.firstName?.trim() ?? '',
    lastName: isPlaceholderLastName(me.lastName) ? '' : me.lastName?.trim() ?? '',
  };

  const onSubmit = async (values: MissingNameFormValues) => {
    try {
      await patchUser(me.id, {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ 'user', me.id, 'basic' ] }),
        queryClient.invalidateQueries({ queryKey: [ 'user', me.id, 'displayName' ] }),
      ]);
      await refetchUser();
      alert.success(copy.saved);
      onClose();
    } catch (error) {
      alert.error(copy.saveError);
    }
  };

  return (
    <Modal
      open
      fullWidth
      maxWidth="sm"
      maxWidthPx="800px"
      onClose={onClose}
      disableBackdropClick
      showCloseIcon={false}
      data-testid="missing-name-modal"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={onSubmit}
      >
        <Form>
          <ModalContent className={classes.content}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Chip label={copy.badge} size="small" className={classes.badge} />
              <IconButton
                onClick={onClose}
                aria-label="close"
                size="small"
                className={classes.closeButton}
                data-testid="close-modal-button"
              >
                <Close />
              </IconButton>
            </Box>
            <Typography variant="h3" className={classes.title}>{copy.title}</Typography>
            <Typography variant="body1" className={classes.text}>{copy.text}</Typography>
            <Box className={classes.fields}>
              <TextInput name="firstName" label={copy.firstName} fullWidth autoFocus />
              <TextInput name="lastName" label={copy.lastName} fullWidth />
            </Box>
            <Typography variant="body2" color="text.secondary" mt={1}>{copy.preferredNameHint}</Typography>
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} mt={3}>
              <SubmitButton variant="contained" color="secondary" fullWidth disabledOnInvalid>
                {copy.save}
              </SubmitButton>
              <Button variant="text" color="secondary" onClick={onClose}>
                {copy.remindLater}
              </Button>
            </Box>
          </ModalContent>
        </Form>
      </Formik>
    </Modal>
  );
};

const useStyles = makeStyles()(theme => ({
  content: {
    padding: theme.spacing(3),
  },
  badge: {
    backgroundColor: theme.palette.warning.background,
    color: theme.palette.warning.darkText,
    border: `1px solid ${theme.palette.warning.border}`,
    fontWeight: 600,
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  title: {
    marginTop: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  text: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '1fr 1fr',
    },
  },
}));
